import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, Link2, Database, AlertCircle } from 'lucide-react';

const ImportHub = () => {
  return (
    <div className="min-h-screen bg-[#0d0f12] ml-56">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="px-3 py-1 rounded-full bg-[#9b6dff]/10 border border-[#9b6dff]/30 text-[#9b6dff] text-xs font-mono tracking-wider">
              📥 IMPORT HUB
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-white mb-2">
            Universal Import Hub
          </h1>
          <p className="text-gray-400">
            Import chats from any platform for QA scoring and analysis
          </p>
        </div>

        {/* Coming Soon Notice */}
        <Card className="bg-[#4f8ef7]/5 border-[#4f8ef7]/30 mb-6">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#4f8ef7] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-white mb-1">
                  Integration Features Coming Soon
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Direct integrations with Zendesk, Freshdesk, Intercom, and custom APIs are under development. 
                  For now, you can manually paste chat transcripts in the QA Scorer.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Planned Integrations */}
        <div className="mb-4">
          <h2 className="text-lg font-medium text-white mb-4">Planned Integrations</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="bg-[#161920] border-[#2a3040]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#34c78a]/10 border border-[#34c78a]/30 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-[#34c78a]" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Zendesk Integration</CardTitle>
                  <Badge variant="outline" className="mt-1 border-[#f5a623]/30 text-[#f5a623]">
                    Coming Soon
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-3">
                Auto-pull tickets and conversations directly from Zendesk for QA scoring
              </p>
              <Button disabled className="w-full" variant="outline">
                Configure Zendesk
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-[#161920] border-[#2a3040]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#34c78a]/10 border border-[#34c78a]/30 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-[#34c78a]" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Freshdesk Integration</CardTitle>
                  <Badge variant="outline" className="mt-1 border-[#f5a623]/30 text-[#f5a623]">
                    Coming Soon
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-3">
                Connect to Freshdesk to import support tickets automatically
              </p>
              <Button disabled className="w-full" variant="outline">
                Configure Freshdesk
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-[#161920] border-[#2a3040]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#34c78a]/10 border border-[#34c78a]/30 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-[#34c78a]" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Intercom Integration</CardTitle>
                  <Badge variant="outline" className="mt-1 border-[#f5a623]/30 text-[#f5a623]">
                    Coming Soon
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-3">
                Import conversations from Intercom for quality analysis
              </p>
              <Button disabled className="w-full" variant="outline">
                Configure Intercom
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-[#161920] border-[#2a3040]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#9b6dff]/10 border border-[#9b6dff]/30 flex items-center justify-center">
                  <Database className="w-5 h-5 text-[#9b6dff]" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Custom API</CardTitle>
                  <Badge variant="outline" className="mt-1 border-[#f5a623]/30 text-[#f5a623]">
                    Coming Soon
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-3">
                Connect any custom API endpoint to import chat data
              </p>
              <Button disabled className="w-full" variant="outline">
                Configure Custom API
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Import */}
        <Card className="bg-[#161920] border-[#2a3040]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#4f8ef7]/10 border border-[#4f8ef7]/30 flex items-center justify-center">
                <Upload className="w-5 h-5 text-[#4f8ef7]" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Bulk CSV Import</CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  Upload multiple chat transcripts at once via CSV file
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-[#2a3040] rounded-lg p-8 text-center mb-4 hover:border-[#4f8ef7]/50 transition-colors cursor-pointer">
              <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
              <p className="text-sm text-gray-400 mb-1">
                Drop CSV file here or click to browse
              </p>
              <p className="text-xs text-gray-600">
                Format: agent_name, agent_id, transcript, date
              </p>
            </div>
            <Button disabled className="w-full" variant="outline">
              Upload CSV File
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportHub;
